import {
    useState,
    useEffect,
    useCallback,
    useContext,
    createContext,
    type FC,
    type ReactNode,
} from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';

import {
    type NftItemTileContextQuery,
    type NftItemTileContextQuery$data,
} from './__generated__/NftItemTileContextQuery.graphql';

type AuthResponse = {
    user?: {
        ethereumWalletAddress?: {
            address: string;
        };
    };
};

type ProviderProps = {
    secondaryMarketRefetch?: (arg?: { refetchDrafts?: boolean }) => void;
    displayToastNotification?: (content: ReactNode, closeAfterTimeout?: boolean) => void;
    profileWalletAddress?: string | null;
    children?: ReactNode;
};

export type NftItemTileContextType = ProviderProps & {
    userWalletAddress: string | null;
    withWalletConnectionEnsuredViewer?: NftItemTileContextQuery$data['viewer'] | null;
};

export const NftItemTileContext = createContext<NftItemTileContextType>({
    userWalletAddress: null,
});

export const NftItemTileContextProvider: FC<ProviderProps> = ({
    children,
    secondaryMarketRefetch,
    displayToastNotification,
    profileWalletAddress,
}) => {
    const environment = useRelayEnvironment();
    const [userWalletAddress, setUserWalletAddress] = useState<string | null>(null);
    const [withWalletConnectionEnsuredViewer, setWithWalletConnectionEnsuredViewer] = useState<
        NftItemTileContextQuery$data['viewer'] | null
    >(null);

    const fetchUserWallet = useCallback(
        (userId: string) => {
            fetchQuery<NftItemTileContextQuery>(
                environment,
                graphql`
                    query NftItemTileContextQuery($userId: String!, $fetchUser: Boolean = true) {
                        viewer {
                            user(userId: $userId) {
                                ethereumWalletAddress {
                                    address
                                }
                            }
                            ...WithWalletConnectionEnsured_viewer
                        }
                    }
                `,
                { userId }
            ).subscribe({
                next: data => {
                    const address = data.viewer.user?.ethereumWalletAddress?.address;
                    if (address) {
                        setUserWalletAddress(address);
                    }
                    setWithWalletConnectionEnsuredViewer(data.viewer);
                },
            });
        },
        [environment]
    );

    useEffect(() => {
        const userId = getBuyerId(document.cookie);

        if (userId) {
            fetchUserWallet(userId);
        }

        addLoginHandlers((authResponse: AuthResponse) => {
            const authPersistedAddress = authResponse.user?.ethereumWalletAddress?.address;
            if (authPersistedAddress) {
                setUserWalletAddress(authPersistedAddress);
            }
        });
    }, [fetchUserWallet]);

    return (
        <NftItemTileContext.Provider
            value={{
                userWalletAddress,
                secondaryMarketRefetch,
                displayToastNotification,
                profileWalletAddress,
                withWalletConnectionEnsuredViewer,
            }}
        >
            {children}
        </NftItemTileContext.Provider>
    );
};

export const useNftItemTileContext = (): NftItemTileContextType => {
    return useContext(NftItemTileContext);
};
